import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

export interface SourceReference {
  id: number;
  name: string;
}

export interface WebSourceReference {
  id: number;
  url: string;
  title: string;
  snippet?: string;
  publishedDate?: string;
}

export interface EntityAnnotation {
  entity_type: 'disease' | 'drug' | 'symptom' | 'procedure';
  entity_id: number;
  entity_name: string;
  text_start: number;
  text_end: number;
  confidence?: number;
}

interface WebSocketChatResponse {
  type: 'chat_response';
  id?: string;
  message?: string;
  text?: string;
  timestamp?: number;
  conversationId?: string;
  isThought?: boolean;
  sources?: SourceReference[];
  webSources?: WebSourceReference[];
  entity_annotations?: EntityAnnotation[];
  referencedAttachmentIds?: string[];
}

interface WebSocketProgressMessage {
  type: 'progress' | 'in_progress' | 'acknowledgment';
  message?: string;
  text?: string;
  timestamp?: number;
  conversationId?: string;
}

interface WebSocketErrorMessage {
  type: 'error';
  message?: string;
}

interface WebSocketAttachmentUpdate {
  type: 'attachment_status_update';
  attachmentId: string;
  status: string;
  error?: string;
}

interface WebSocketThoughtUpdate {
  type: 'thought_update';
  conversationId: string;
  messageId: string;
  thought: string;
  status: 'thinking' | 'processing' | 'complete';
  timestamp: number;
}

interface WebSocketPong {
  type: 'pong';
}

interface WebSocketStreamChunk {
  type: 'stream_chunk';
  chunk: string;
  conversationId: string;
  timestamp: string;
}

interface WebSocketCaseCreated {
  type: 'case_created';
  conversationId: string;
  caseId: string;
  status: string;
  timestamp: number;
}

interface WebSocketCaseUpdated {
  type: 'case_updated';
  conversationId: string;
  caseId: string;
  status: string;
  changes: string[];
  timestamp: number;
}

interface WebSocketCaseStatusChanged {
  type: 'case_status_changed';
  conversationId: string;
  caseId: string;
  oldStatus: string;
  newStatus: string;
  timestamp: number;
}

interface WebSocketCaseProcessingComplete {
  type: 'case_processing_complete';
  conversationId: string;
  caseId: string;
  result: {
    symptoms_count: number;
    diagnoses_count: number;
    message: string;
  };
  timestamp: number;
}

interface WebSocketToolCall {
  type: 'tool_call';
  toolName: string;
  conversationId: string;
  timestamp: string;
}

type WebSocketMessage =
  | WebSocketChatResponse
  | WebSocketProgressMessage
  | WebSocketErrorMessage
  | WebSocketAttachmentUpdate
  | WebSocketThoughtUpdate
  | WebSocketPong
  | WebSocketStreamChunk
  | WebSocketToolCall
  | WebSocketCaseCreated
  | WebSocketCaseUpdated
  | WebSocketCaseStatusChanged
  | WebSocketCaseProcessingComplete;

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'copilot';
  timestamp: Date;
  conversationId?: string;
  isProgress?: boolean; // Mark message as "in progress"
  isThought?: boolean; // Mark message as a thought/analysis message (legacy, kept for compatibility)
  isThinking?: boolean; // Mark message as real-time disposable thought with special styling
  sources?: SourceReference[];
  webSources?: WebSourceReference[];
  entity_annotations?: EntityAnnotation[];
  attachmentIds?: string[]; // Array of attachment IDs associated with this message
}

export interface UseChatReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  conversationId: string;
  sendMessage: (message: string, attachmentIds?: string[]) => void;
  clearMessages: () => void;
  loadInitialMessages: (initialMessages: ChatMessage[]) => void;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Custom hook for managing WebSocket chat functionality
 * Now uses WebSocketContext for singleton connection management
 */
export const useChat = (initialConversationId: string): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const conversationIdRef = useRef<string>(initialConversationId);

  // Streaming chunk batching - accumulate chunks and flush periodically to reduce re-renders
  const streamingBufferRef = useRef<string>('');
  const streamingFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamingTimestampRef = useRef<string | null>(null);
  const streamingConversationIdRef = useRef<string | null>(null);
  const STREAMING_FLUSH_INTERVAL = 50; // ms - balance between responsiveness and performance

  // Use WebSocket context instead of managing connection directly
  const {
    isConnected,
    isConnecting,
    error,
    sendMessage: wsSendMessage,
    onMessage
  } = useWebSocket();

  // Flush accumulated streaming chunks to state
  const flushStreamingBuffer = useCallback(() => {
    if (streamingBufferRef.current === '') return;

    const bufferedContent = streamingBufferRef.current;
    const timestamp = streamingTimestampRef.current;
    const conversationId = streamingConversationIdRef.current;

    // Clear the buffer
    streamingBufferRef.current = '';
    streamingFlushTimeoutRef.current = null;

    setMessages(prev => {
      const streamingMessageId = 'streaming';
      const existingIndex = prev.findIndex(msg => msg.id === streamingMessageId);

      if (existingIndex >= 0) {
        // Append buffered content to existing streaming message
        const updatedMessages = [...prev];
        updatedMessages[existingIndex] = {
          ...updatedMessages[existingIndex],
          text: updatedMessages[existingIndex].text + bufferedContent,
        };
        return updatedMessages;
      } else {
        // Create new streaming message (remove any progress messages first)
        const withoutProgress = prev.filter(msg => !msg.isProgress);
        const streamingMessage: ChatMessage = {
          id: streamingMessageId,
          text: bufferedContent,
          sender: 'copilot',
          timestamp: new Date(timestamp || Date.now()),
          conversationId: conversationId || undefined,
          isProgress: false,
        };
        return [...withoutProgress, streamingMessage];
      }
    });
  }, []);

  const handleMessage = useCallback((data: WebSocketMessage) => {
    console.log('Received WebSocket message:', data);

    // Handle different message types
    if (data.type === 'chat_response') {
      // Clear any pending streaming buffer/timeout - final response supersedes streaming
      if (streamingFlushTimeoutRef.current) {
        clearTimeout(streamingFlushTimeoutRef.current);
        streamingFlushTimeoutRef.current = null;
      }
      streamingBufferRef.current = '';

      const newMessage: ChatMessage = {
        id: data.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: data.message || data.text || 'Received response from Copilot',
        sender: 'copilot',
        timestamp: new Date(data.timestamp || Date.now()),
        conversationId: data.conversationId,
        isProgress: false,
        isThought: data.isThought || false,
        sources: data.sources || [],
        webSources: data.webSources || [],
        entity_annotations: data.entity_annotations || [],
        // Include referenced attachments if the LLM referenced any
        ...(data.referencedAttachmentIds && { attachmentIds: data.referencedAttachmentIds }),
      };

      // If this is a final response, remove progress messages (which includes thinking), streaming messages
      setMessages(prev => {
        const withoutProgressAndStreaming = prev.filter(msg =>
          !msg.isProgress &&
          msg.id !== 'streaming'
        );
        return [...withoutProgressAndStreaming, newMessage];
      });
    } else if (data.type === 'attachment_status_update') {
      // Handle attachment status updates by triggering a UI refresh
      // The ConversationDashboardModal will pick up the changes when re-queried
      console.log(`Attachment ${data.attachmentId} status updated to: ${data.status}`, data.error ? `Error: ${data.error}` : '');
      // For now, we just log it. In the future, we could trigger a refresh of attachment status in the modal
      
    } else if (data.type === 'progress' || data.type === 'in_progress' || data.type === 'acknowledgment') {
      const progressMessage: ChatMessage = {
        id: 'progress', // Use fixed ID to replace existing progress message
        text: data.message || data.text || 'Processing...',
        sender: 'copilot',
        timestamp: new Date(data.timestamp || Date.now()),
        conversationId: data.conversationId,
        isProgress: true,
      };

      // Replace any existing progress message or add new one
      setMessages(prev => {
        const withoutProgress = prev.filter(msg => !msg.isProgress);
        return [...withoutProgress, progressMessage];
      });
    } else if (data.type === 'stream_chunk') {
      // Handle streaming chunks with batching to reduce re-renders
      // Accumulate chunks in buffer and flush periodically

      // Store metadata for when we create the message
      streamingTimestampRef.current = data.timestamp;
      streamingConversationIdRef.current = data.conversationId;

      // Add chunk to buffer
      streamingBufferRef.current += data.chunk;

      // Schedule a flush if not already scheduled
      if (!streamingFlushTimeoutRef.current) {
        streamingFlushTimeoutRef.current = setTimeout(() => {
          flushStreamingBuffer();
        }, STREAMING_FLUSH_INTERVAL);
      }
    } else if (data.type === 'thought_update') {
      // Handle real-time thought updates - these REPLACE the progress system entirely
      console.log('Received thought update:', data.thought);
      
      const thoughtMessage: ChatMessage = {
        id: 'progress', // Use same ID as progress to ensure replacement
        text: data.thought,
        sender: 'copilot',
        timestamp: new Date(data.timestamp || Date.now()),
        conversationId: data.conversationId,
        isProgress: true, // Use progress visual treatment but with thought styling
        isThinking: true, // Flag for special thought styling (cursive, light background)
      };

      // Replace any existing progress message with the thought
      setMessages(prev => {
        const withoutProgress = prev.filter(msg => !msg.isProgress);
        return [...withoutProgress, thoughtMessage];
      });
    } else if (data.type === 'tool_call') {
      // Handle tool call notifications - show as inline chip during streaming
      console.log('Tool call detected:', data.toolName);

      // Store metadata for when we create the message
      streamingTimestampRef.current = data.timestamp;
      streamingConversationIdRef.current = data.conversationId;

      // Add a tool marker to the streaming buffer
      // This will be rendered as a chip by the ChatInterface
      streamingBufferRef.current += `\n[[TOOL:${data.toolName}]]\n`;

      // Flush immediately so the tool chip appears right away
      flushStreamingBuffer();
    } else if (data.type === 'error') {
      // Remove progress messages on error - error handling is now done by WebSocket context
      setMessages(prev => prev.filter(msg => !msg.isProgress));
    } else if (data.type === 'case_created') {
      // Handle case creation notifications - only log, don't add to chat
      console.log('Case created:', data.caseId);
      // Case button state will be updated by ChatInterface.tsx
    } else if (data.type === 'case_updated') {
      // Handle case update notifications - only log, don't add to chat
      console.log('Case updated:', data.caseId, 'changes:', data.changes);
      // Case button state will be updated by ChatInterface.tsx
    } else if (data.type === 'case_status_changed') {
      // Handle case status change notifications - only log, don't add to chat
      console.log('Case status changed:', data.caseId, `${data.oldStatus} -> ${data.newStatus}`);
      // Case button state will be updated by ChatInterface.tsx
    } else if (data.type === 'case_processing_complete') {
      // Handle case processing completion notifications - only log, don't add to chat
      console.log('Case processing complete:', data.caseId);
      // Case button state will be updated by ChatInterface.tsx
    } else if (data.type === 'pong') {
      // Handle ping/pong for connection health
      console.log('Received pong from server');
    }
  }, [flushStreamingBuffer]);

  const sendMessage = useCallback((messageText: string, attachmentIds?: string[]) => {
    console.log('useChat sendMessage called - isConnected:', isConnected, 'isConnecting:', isConnecting);
    
    if (!isConnected && !isConnecting) {
      console.error('Not connected to chat service and not connecting');
      return;
    }

    // Capture the current conversation ID to avoid stale closure issues
    const currentConversationId = conversationIdRef.current;

    // Add user message to the chat immediately
    const userMessage: ChatMessage = {
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      conversationId: currentConversationId,
      attachmentIds: attachmentIds || [],
    };

    setMessages(prev => [...prev, userMessage]);

    // Simplified send logic - no retries to avoid stale connection issues
    try {
      console.log(`Sending message with conversation ID: ${currentConversationId}`, attachmentIds ? `and ${attachmentIds.length} attachments` : '');
      wsSendMessage(messageText, currentConversationId, attachmentIds);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove the user message from UI since send failed
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      // Could show an error message to the user here
    }
  }, [isConnected, isConnecting, wsSendMessage]);

  const clearMessages = useCallback(async () => {
    console.log('Clearing messages...');
    setMessages([]);
  }, []);

  const loadInitialMessages = useCallback((initialMessages: ChatMessage[]) => {
    console.log('Loading initial messages:', initialMessages.length);
    setMessages(initialMessages);
  }, []);

  // Dummy connect/disconnect functions for API compatibility
  const connect = useCallback(async () => {
    console.log('Connect called - WebSocket connection is managed by context');
  }, []);

  const disconnect = useCallback(() => {
    console.log('Disconnect called - WebSocket connection is managed by context');
  }, []);

  // Set up WebSocket message handler
  useEffect(() => {
    const removeMessageHandler = onMessage(handleMessage);
    return removeMessageHandler;
  }, [handleMessage, onMessage]);

  // Handle conversationId changes (e.g., when navigating between conversations)
  useEffect(() => {
    const previousConversationId = conversationIdRef.current;

    if (initialConversationId) {
      // Only update and potentially clear if the ID actually changed
      if (previousConversationId !== initialConversationId) {
        console.log(`Conversation ID changed from ${previousConversationId} to ${initialConversationId}`);
        conversationIdRef.current = initialConversationId;

        // Clear streaming buffer when switching conversations
        if (streamingFlushTimeoutRef.current) {
          clearTimeout(streamingFlushTimeoutRef.current);
          streamingFlushTimeoutRef.current = null;
        }
        streamingBufferRef.current = '';

        // Clear messages when switching to a truly different conversation
        // Don't clear on initial mount (when previousConversationId is undefined)
        if (previousConversationId !== undefined) {
          console.log('Clearing messages for different conversation');
          setMessages([]);
        }
      }
    }
  }, [initialConversationId]);

  // Cleanup streaming buffer timeout on unmount
  useEffect(() => {
    return () => {
      if (streamingFlushTimeoutRef.current) {
        clearTimeout(streamingFlushTimeoutRef.current);
      }
    };
  }, []);

  return {
    messages,
    isConnected,
    isConnecting,
    error,
    conversationId: conversationIdRef.current,
    sendMessage,
    clearMessages,
    loadInitialMessages,
    connect,
    disconnect,
  };
};
